import { Component, OnInit } from '@angular/core';
import {
    DailySchedule,
    IntervalConfig,
    NotificationAdminService,
    NotificationTemplate,
    PushConfig,
    SendNotification
} from '@services/notification-admin.service';

type Tab = 'send' | 'templates' | 'intervals' | 'schedules' | 'config';

@Component({
    selector: 'app-notifications-page',
    templateUrl: './notifications-page.component.html'
})
export class NotificationsPageComponent implements OnInit {
    tab: Tab = 'send';
    message = '';
    error = '';

    templates: NotificationTemplate[] = [];
    intervals: IntervalConfig[] = [];
    schedules: DailySchedule[] = [];

    send: SendNotification = this.emptySend();
    sendTarget: 'user' | 'all' = 'all';

    tpl: NotificationTemplate = this.emptyTemplate();
    interval: IntervalConfig = this.emptyInterval();
    schedule: DailySchedule = this.emptySchedule();
    config: PushConfig = { provider: 1, credentialsJson: '', bundleId: '', environment: 'production' };

    constructor(private api: NotificationAdminService) {}

    ngOnInit() {
        this.loadAll();
    }

    setTab(t: Tab) {
        this.tab = t;
        this.message = '';
        this.error = '';
    }

    loadAll() {
        this.api.listTemplates().subscribe(r => (this.templates = r), e => this.fail(e));
        this.api.listIntervals().subscribe(r => (this.intervals = r), e => this.fail(e));
        this.api.listSchedules().subscribe(r => (this.schedules = r), e => this.fail(e));
    }

    // ---- Send ----
    doSend() {
        const dto: SendNotification = {
            ...this.send,
            userId: this.sendTarget === 'user' ? this.send.userId : null,
            sendToAll: this.sendTarget === 'all'
        };
        if (dto.sendToAll && !confirm('Bildirim TÜM kullanıcılara gönderilecek. Emin misiniz?')) return;
        this.api.send(dto).subscribe(
            r => this.ok(`Hedef: ${r.totalTargets}, gönderilen: ${r.sentCount}, başarısız: ${r.failedCount}`),
            e => this.fail(e)
        );
    }

    // ---- Templates ----
    saveTemplate() {
        const t = this.tpl;
        const req = t.id ? this.api.updateTemplate(t.id, t) : this.api.createTemplate(t);
        req.subscribe(() => {
            this.ok('Şablon kaydedildi');
            this.tpl = this.emptyTemplate();
            this.loadAll();
        }, e => this.fail(e));
    }
    editTemplate(t: NotificationTemplate) { this.tpl = { ...t }; }
    resetTemplate() { this.tpl = this.emptyTemplate(); }
    deleteTemplate(t: NotificationTemplate) {
        if (!confirm(`"${t.name}" silinsin mi?`)) return;
        this.api.deleteTemplate(t.id!).subscribe(() => { this.ok('Silindi'); this.loadAll(); }, e => this.fail(e));
    }

    // ---- Intervals ----
    saveInterval() {
        const c = this.interval;
        const req = c.id ? this.api.updateInterval(c.id, c) : this.api.createInterval(c);
        req.subscribe(() => {
            this.ok('Aralık ayarı kaydedildi');
            this.interval = this.emptyInterval();
            this.loadAll();
        }, e => this.fail(e));
    }
    editInterval(c: IntervalConfig) { this.interval = { ...c }; }
    deleteInterval(c: IntervalConfig) {
        if (!confirm(`"${c.name}" silinsin mi?`)) return;
        this.api.deleteInterval(c.id!).subscribe(() => { this.ok('Silindi'); this.loadAll(); }, e => this.fail(e));
    }

    // ---- Daily schedules ----
    saveSchedule() {
        const s = this.schedule;
        const req = s.id ? this.api.updateSchedule(s.id, s) : this.api.createSchedule(s);
        req.subscribe(() => {
            this.ok('Günlük zamanlama kaydedildi');
            this.schedule = this.emptySchedule();
            this.loadAll();
        }, e => this.fail(e));
    }
    editSchedule(s: DailySchedule) { this.schedule = { ...s }; }
    toggleSchedule(s: DailySchedule) {
        this.api.updateSchedule(s.id!, { active: !s.active }).subscribe(() => this.loadAll(), e => this.fail(e));
    }
    deleteSchedule(s: DailySchedule) {
        if (!confirm(`"${s.name}" silinsin mi?`)) return;
        this.api.deleteSchedule(s.id!).subscribe(() => { this.ok('Silindi'); this.loadAll(); }, e => this.fail(e));
    }

    // ---- Provider credentials ----
    saveConfig() {
        if (!confirm('Mevcut aktif credential pasif yapılıp yenisi kaydedilecek. Devam edilsin mi?')) return;
        this.api.saveConfig(this.config).subscribe(() => {
            this.ok('Credential kaydedildi');
            this.config.credentialsJson = '';
        }, e => this.fail(e));
    }

    priorityLabel(p: number) { return ['Düşük', 'Normal', 'Yüksek'][p] ?? p; }

    private ok(m: string) { this.message = m; this.error = ''; }
    private fail(e: any) {
        this.error = typeof e?.error === 'string' && e.error ? e.error : `Hata (${e?.status ?? '?'})`;
        this.message = '';
    }

    private emptySend(): SendNotification { return { userId: null, sendToAll: false, title: '', body: '', priority: 1 }; }
    private emptyTemplate(): NotificationTemplate { return { name: '', title: '', body: '', priority: 1 }; }
    private emptyInterval(): IntervalConfig {
        return { name: '', minIntervalHours: 24, maxIntervalHours: 24, templateId: null, quietHoursEnabled: false, quietHoursStart: '22:00', quietHoursEnd: '08:00' };
    }
    private emptySchedule(): DailySchedule { return { name: '', hour: 9, minute: 0, templateId: 0 }; }
}
